import { useEffect, useState } from "react";
import { Ban, Loader2, RotateCcw, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";

const Toggle = ({ on, onClick, icon: Icon, label, onLabel, testId, tone }) => (
  <button onClick={onClick} data-testid={testId}
    className={`h-9 px-2 whitespace-nowrap rounded-full text-[11px] font-bold inline-flex items-center justify-center gap-1 border-2 transition-colors active:scale-95 ${on ? `${tone} text-white border-transparent` : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
    <Icon className="w-3.5 h-3.5" /> {on ? onLabel : label}
  </button>
);

function Row({ u, place, onPatch }) {
  const set = async (key, val) => {
    try { const { data } = await api.put(`/admin/users/${u.user_id}/exclusions`, { [key]: val }); onPatch({ points: data.points, frozen: data.frozen }); toast.success("Gespeichert"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const reset = async () => {
    if (!window.confirm(`Guthaben von ${u.name} (${u.balance.toLocaleString("de-DE")} Punkte) wirklich auf 0 setzen?`)) return;
    try { await api.post(`/admin/users/${u.user_id}/points/reset`); onPatch({ balance: 0 }); toast.success(`Guthaben von ${u.name} auf 0 gesetzt`); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <li className="py-4 space-y-3" data-testid={`richest-row-${u.user_id}`}>
      <div className="flex items-center gap-3">
        <span className="w-7 text-center font-extrabold text-slate-400 tabular-nums">{place}</span>
        <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-10 h-10" noStory />
        <div className="flex-1 min-w-0"><p className="font-bold text-sm truncate"><UserLink id={u.user_id}>{u.name}</UserLink></p><p className="text-xs text-slate-400 truncate">{u.email}</p></div>
        <span className="font-extrabold text-rose-600 tabular-nums" data-testid={`richest-balance-${u.user_id}`}>{u.balance.toLocaleString("de-DE")}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 pl-10">
        <Toggle on={u.frozen} onClick={() => set("frozen", !u.frozen)} icon={Snowflake} label="Einfrieren" onLabel="Eingefroren" tone="bg-sky-500" testId={`richest-freeze-${u.user_id}`} />
        <button onClick={reset} disabled={u.balance <= 0} data-testid={`richest-reset-${u.user_id}`} className="h-9 px-3 rounded-full text-xs font-bold inline-flex items-center justify-center gap-1.5 border-2 border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40 active:scale-95 transition-transform"><RotateCcw className="w-3.5 h-3.5" /> Auf 0</button>
        <Toggle on={u.points} onClick={() => set("points", !u.points)} icon={Ban} label="Sammeln sperren" onLabel="Gesperrt" tone="bg-rose-600" testId={`richest-block-${u.user_id}`} />
      </div>
    </li>
  );
}

export function RichestAdmin() {
  const [list, setList] = useState(null);
  const [at, setAt] = useState(null);
  useEffect(() => {
    const load = (first) => api.get("/admin/points/richest").then(({ data }) => { setList(data); setAt(new Date()); }).catch((e) => { if (first) { setList([]); toast.error(formatErr(e)); } });
    load(true);
    const iv = setInterval(() => { if (!document.hidden) load(false); }, 20000);
    return () => clearInterval(iv);
  }, []);
  const total = (list || []).reduce((s, x) => s + (x.balance || 0), 0);
  const patch = (id, p) => setList((l) => l.map((x) => (x.user_id === id ? { ...x, ...p } : x)));
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-2" data-testid="richest-admin">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h2 className="text-lg font-extrabold">Top 100 Kontostände</h2>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-600" data-testid="richest-live"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live{at ? ` · ${at.toLocaleTimeString("de-DE")}` : ""}</span>
        {list?.length > 0 && <span className="ml-auto text-xs text-slate-500" data-testid="richest-total">Zusammen: <b>{total.toLocaleString("de-DE")}</b> Punkte</span>}
      </div>
      <ul className="text-xs text-slate-500 space-y-0.5">
        <li><b>Einfrieren:</b> Guthaben bleibt, kann aber nicht eingelöst oder eingesetzt werden. Sammeln geht weiter.</li>
        <li><b>Auf 0:</b> setzt das Guthaben sofort auf 0 (steht im Punkte-Verlauf, Mitglied bekommt eine Mitteilung).</li>
        <li><b>Sammeln sperren:</b> keinerlei neue Punkte mehr und nichts einlösen.</li>
      </ul>
      {!list ? <Loader2 className="w-6 h-6 animate-spin text-rose-400 mx-auto" /> : (
        <ol className="divide-y divide-pink-50" data-testid="richest-list">
          {list.map((u, i) => <Row key={u.user_id} u={u} place={i + 1} onPatch={(p) => patch(u.user_id, p)} />)}
        </ol>
      )}
    </div>
  );
}
